// BedtimePledgeCard — surfaces between 20:00 and 02:00 if no pledge has
// been written for today. One textarea, one Save button. The same
// surface appears after the check-in's optional pledge step if it was
// skipped.
//
// This is the daily identity beat: yesterday-you talking to today-you.
// Cheap to build, durable behavior-change lever (Vangeli & West 2012;
// I Am Sober's most-praised mechanic).
//
// Phase C.1 §P10.5 adds a quiet sibling: a "leave a note for
// tomorrow-you" link below the Save button that opens the
// HandoffComposer. Pledges are aspirational ("the why"); hand-offs
// are operational ("if X happens, do Y") — two different organs that
// share a bedtime surface. The link is intentionally low-weight: a
// tertiary button, not a CTA, so it doesn't compete with Save.

import { useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Textarea } from '../../components/ui/Textarea';
import { useToast } from '../../components/ui/Toast';
import { apiPost } from '../../lib/api';
import { apiErrorMessage, isQueuedOffline } from '../../lib/apiErrors';
import { HandoffComposer } from './HandoffComposer';

interface BedtimePledgeCardProps {
  onSaved: () => void;
  /**
   * Fires after a hand-off note is saved through the composer. Parent
   * uses this to refetch `/api/handoff` so the new note becomes the
   * Zone B priority next morning (or immediately, if the user reopens
   * Today before the night rolls over).
   */
  onHandoffSaved?: () => void;
}

export function BedtimePledgeCard({ onSaved, onHandoffSaved }: BedtimePledgeCardProps) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);

  async function save() {
    const trimmed = text.trim();
    if (trimmed.length < 4) return;
    setBusy(true);
    try {
      await apiPost('/api/pledge', { text: trimmed });
      toast.push('Saved. Tomorrow-you will see this first.');
      onSaved();
    } catch (err) {
      if (!isQueuedOffline(err)) {
        toast.push(apiErrorMessage(err, 'Could not save — try again.'), { tone: 'error' });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Card accentLeft className="bedtime-pledge-card">
        <p className="bedtime-pledge-prompt">One sentence on the why for tomorrow?</p>
        <Textarea
          label="One sentence — tomorrow-you sees this first"
          value={text}
          maxLength={280}
          onChange={(e) => setText(e.target.value)}
          placeholder="Because…"
        />
        {/* Phase 27.4 — new community story submissions are paused while
         *  we measure retention. The archive of previously approved stories
         *  continues to surface on Today; the submission UX is hidden until
         *  the §P9.7 retention gates open. */}
        <Button
          size="md"
          block
          onClick={() => void save()}
          loading={busy}
          disabled={text.trim().length < 4}
          className="bedtime-pledge-save"
        >
          Save for tomorrow
        </Button>
        {/* Phase C.1 §P10.5 — operational sibling to the aspirational
         *  pledge above. Styled as a quiet tertiary link so the primary
         *  Save action keeps the visual weight. */}
        <Button
          variant="tertiary"
          size="md"
          onClick={() => setComposerOpen(true)}
          style={{
            fontStyle: 'italic',
            fontWeight: 'normal',
            alignSelf: 'center',
          }}
        >
          Or: leave a note for tomorrow-you →
        </Button>
      </Card>
      <HandoffComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        onSaved={() => {
          onHandoffSaved?.();
        }}
      />
    </>
  );
}
